import { useEffect, useState } from 'react';
import type { DiscountReason } from '@equinox/shared';
import { useI18n } from '../../i18n/I18nContext.js';
import { discountReasonsApi } from '../../api/resources.js';
import { ApiError } from '../../lib/apiClient.js';
import { Banner } from '../../components/Banner.js';
import { Modal } from '../../components/Modal.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';

function ReasonFormModal({
  reason,
  onClose,
  onSaved,
}: {
  reason: DiscountReason | null;
  onClose(): void;
  onSaved(r: DiscountReason): void;
}) {
  const { t } = useI18n();
  const [label, setLabel] = useState(reason?.label ?? '');
  const [requiresNote, setRequiresNote] = useState(reason?.requiresNote ?? false);
  // '' = no standard (legal). Digits with up to two decimals, 0-100.
  const [defaultPctRaw, setDefaultPctRaw] = useState(
    reason?.defaultPct != null ? String(reason.defaultPct) : '',
  );
  const parsedDefaultPct = defaultPctRaw.trim() === '' ? null : Number(defaultPctRaw);
  const [minSubtotalRaw, setMinSubtotalRaw] = useState(
    reason?.minSubtotalCents != null ? (reason.minSubtotalCents / 100).toFixed(2) : '',
  );
  const parsedMinSubtotal =
    minSubtotalRaw.trim() === '' ? null : Math.round(Number(minSubtotalRaw) * 100);
  const minSubtotalValid =
    parsedMinSubtotal === null ||
    (/^\d{1,7}(\.\d{1,2})?$/.test(minSubtotalRaw.trim()) && parsedMinSubtotal >= 0);
  const defaultPctValid =
    parsedDefaultPct === null ||
    (/^\d{1,3}(\.\d{1,2})?$/.test(defaultPctRaw.trim()) && parsedDefaultPct >= 0 && parsedDefaultPct <= 100);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canSave = label.trim() && defaultPctValid && minSubtotalValid && !saving;

  async function submit() {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      const saved = reason
        ? await discountReasonsApi.update(reason.id, {
            label: label.trim(),
            requiresNote,
            defaultPct: parsedDefaultPct,
            minSubtotalCents: parsedMinSubtotal,
          })
        : await discountReasonsApi.create({
            label: label.trim(),
            requiresNote,
            defaultPct: parsedDefaultPct,
            minSubtotalCents: parsedMinSubtotal,
          });
      onSaved(saved);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title={reason ? t('common.edit') : t('discountReasons.new')} onClose={onClose}>
      {error && <p className="field-error">{error}</p>}
      <label htmlFor="reason-label">
        {t('discountReasons.label')}
        <input id="reason-label" value={label} onChange={(e) => setLabel(e.target.value)} required />
      </label>
      <div className="field">
        <label htmlFor="reason-default-pct">{t('discountReasons.defaultPct')}</label>
        <span className="input-affix input-affix--suffix">
          <input
            id="reason-default-pct"
            type="text"
            inputMode="decimal"
            className="numeric"
            style={{ textAlign: 'right' }}
            value={defaultPctRaw}
            aria-invalid={!defaultPctValid}
            onChange={(e) => setDefaultPctRaw(e.target.value.replace(/[^\d.]/g, ''))}
            disabled={saving}
          />
          <span className="input-affix__symbol" aria-hidden="true">
            %
          </span>
        </span>
        <span className="field-hint">{t('discountReasons.defaultPctHint')}</span>
      </div>
      <div className="field">
        <label htmlFor="reason-min-subtotal">{t('discountReasons.minSubtotal')}</label>
        <span className="input-affix input-affix--prefix">
          <span className="input-affix__symbol" aria-hidden="true">
            $
          </span>
          <input
            id="reason-min-subtotal"
            type="text"
            inputMode="decimal"
            className="numeric"
            style={{ textAlign: 'right' }}
            value={minSubtotalRaw}
            aria-invalid={!minSubtotalValid}
            onChange={(e) => setMinSubtotalRaw(e.target.value.replace(/[^\d.]/g, ''))}
            disabled={saving}
          />
        </span>
        <span className="field-hint">{t('discountReasons.minSubtotalHint')}</span>
      </div>
      <label htmlFor="reason-note" style={{ flexDirection: 'row', alignItems: 'center', gap: 'var(--space-2)' }}>
        <input
          id="reason-note"
          type="checkbox"
          checked={requiresNote}
          onChange={(e) => setRequiresNote(e.target.checked)}
          style={{ minHeight: 'auto', width: 'auto' }}
        />
        {t('discountReasons.requiresNote')}
      </label>
      <div className="modal__actions">
        <button type="button" className="btn btn--secondary" onClick={onClose} disabled={saving}>
          {t('common.cancel')}
        </button>
        <button type="button" className="btn btn--primary" disabled={!canSave} onClick={() => void submit()}>
          {t('discountReasons.save')}
        </button>
      </div>
    </Modal>
  );
}

export function DiscountReasonsPage() {
  const { t } = useI18n();
  const [reasons, setReasons] = useState<DiscountReason[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<DiscountReason | 'new' | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<DiscountReason | null>(null);

  async function reload() {
    setLoading(true);
    setError(null);
    try {
      setReasons(await discountReasonsApi.list());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
      <div className="page-header">
        <div>
          <h1>{t('discountReasons.title')}</h1>
          <p>{t('discountReasons.subtitle')}</p>
        </div>
        <button type="button" className="btn btn--primary" onClick={() => setEditing('new')}>
          {t('discountReasons.new')}
        </button>
      </div>

      {error && <Banner kind="danger">{error}</Banner>}

      {loading ? (
        <p>{t('common.loading')}</p>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{t('discountReasons.label')}</th>
                <th>{t('discountReasons.requiresNote')}</th>
                <th className="numeric">{t('discountReasons.defaultPct')}</th>
                <th className="numeric">{t('discountReasons.minSubtotal')}</th>
                <th>{t('common.status')}</th>
                <th>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {reasons.map((r) => (
                <tr key={r.id}>
                  <td>{r.label}</td>
                  <td>{r.requiresNote ? t('common.yes') : t('common.no')}</td>
                  <td className="numeric">{r.defaultPct != null ? `${r.defaultPct} %` : '—'}</td>
                  <td className="numeric">
                    {r.minSubtotalCents != null ? `$ ${(r.minSubtotalCents / 100).toFixed(2)}` : '—'}
                  </td>
                  <td>
                    <span className={`badge badge--${r.active ? 'ok' : 'neutral'}`}>
                      {r.active ? t('common.active') : t('common.inactive')}
                    </span>
                  </td>
                  <td style={{ display: 'flex', gap: 'var(--space-2)' }}>
                    <button type="button" className="btn btn--ghost btn--sm" onClick={() => setEditing(r)}>
                      {t('common.edit')}
                    </button>
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      onClick={() => setDeactivateTarget(r)}
                    >
                      {r.active ? t('discountReasons.deactivate') : t('discountReasons.activate')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <ReasonFormModal
          reason={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(saved) => {
            setReasons((prev) => {
              const exists = prev.some((r) => r.id === saved.id);
              return exists ? prev.map((r) => (r.id === saved.id ? saved : r)) : [...prev, saved];
            });
            setEditing(null);
          }}
        />
      )}

      {deactivateTarget && (
        <ConfirmDialog
          title={t('discountReasons.deactivate')}
          consequence={t('discountReasons.deactivateConfirm')}
          danger={deactivateTarget.active}
          onCancel={() => setDeactivateTarget(null)}
          onConfirm={async () => {
            const target = deactivateTarget;
            setDeactivateTarget(null);
            try {
              const updated = target.active
                ? await discountReasonsApi.deactivate(target.id)
                : await discountReasonsApi.activate(target.id);
              setReasons((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
            } catch (err) {
              setError(err instanceof ApiError ? err.message : t('common.error'));
            }
          }}
        />
      )}
    </div>
  );
}
